import React, { useState } from "react";
import {
  Badge,
  Box,
  Collapse,
  Flex,
  HStack,
  Spinner,
  Text,
} from "@chakra-ui/react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { formatRunDate, formatTarget, runsForScope } from "../../../../utils/aiIngestTargets";
import { useAiIngestRun, useAiIngestRuns } from "../../../../hooks/query/questionnaire";

/**
 * The AI-assisted ingests into this scope, each opening to the audit trail it was committed with.
 *
 * This closes a dangling reference. `AiIngestFacade.appendMethodology` writes a two-line pointer into
 * `methodology_map` ending "Full trail: ai-ingest run #12", and until now the product gave no way to
 * open run #12 — the full document existed only on the record and in the pre-commit preview, so after
 * committing nobody could read what had been filed.
 *
 * Renders nothing when the scope has no runs, which is the normal case for a Scope 3 category card:
 * ai-ingest writes only Scope 1 and Scope 2.
 */
export function AiIngestHistory({ questionnaireId, scopeKey }) {
  const [openRunId, setOpenRunId] = useState(null);
  const { data: runs, isLoading } = useAiIngestRuns(questionnaireId);

  const scoped = runsForScope(runs, scopeKey);

  // Deliberately silent while loading and when empty. This sits inside a card about a human's own
  // methodology notes; a spinner or an "in this scope: none" line on every task that has never used AI
  // mapping would be noise on the majority of tasks.
  if (isLoading || scoped.length === 0) return null;

  return (
    <Box mt="18px" pt="16px" borderTop="1px solid" borderColor="gray.100">
      <HStack spacing={2} mb="10px">
        <Text fontSize="11px" fontWeight="700" letterSpacing="0.04em" color="gray.500"
              textTransform="uppercase">
          AI-assisted ingests
        </Text>
        <Badge fontSize="10px" variant="subtle" bg="gray.100" color="gray.600" borderRadius="full" px={2}>
          {scoped.length}
        </Badge>
      </HStack>

      <Box display="flex" flexDirection="column" gap="8px">
        {scoped.map((run) => (
          <RunRow
            key={run.id}
            run={run}
            questionnaireId={questionnaireId}
            isOpen={openRunId === run.id}
            onToggle={() => setOpenRunId(openRunId === run.id ? null : run.id)}
          />
        ))}
      </Box>
    </Box>
  );
}

const RunRow = ({ run, questionnaireId, isOpen, onToggle }) => {
  // Only the opened run's document is fetched — the trail and the step log are the two unbounded
  // fields on the record, and the list endpoint nulls them for exactly that reason.
  const { data: detail, isLoading, isError } = useAiIngestRun(
    { questionnaireId, runId: isOpen ? run.id : null }
  );

  return (
    <Box border="1px solid" borderColor="gray.150" borderRadius="10px" bg="white" overflow="hidden">
      <Flex
        as="button"
        type="button"
        onClick={onToggle}
        w="100%"
        align="center"
        justify="space-between"
        px="14px"
        py="10px"
        gap={3}
        textAlign="left"
        _hover={{ bg: "gray.50" }}
      >
        <HStack spacing={3} minW={0}>
          <Text fontSize="13px" fontWeight="600" color="darkTeal" noOfLines={1}>
            {formatTarget(run.target)}
          </Text>
          <Text fontSize="12px" color="gray.600" flexShrink={0}>
            {run.rowsWritten} {run.rowsWritten === 1 ? "row" : "rows"}
            {run.rowsReplaced > 0 && ` (replaced ${run.rowsReplaced})`}
          </Text>
          {run.sourceFileName && (
            <Text fontSize="12px" color="gray.500" noOfLines={1}>
              {run.sourceFileName}
            </Text>
          )}
        </HStack>
        <HStack spacing={3} flexShrink={0}>
          {formatRunDate(run.createdDate) && (
            <Text fontSize="11px" color="gray.500">
              {formatRunDate(run.createdDate)}
            </Text>
          )}
          <Text fontSize="11px" fontWeight="600" color="gray.500">
            {isOpen ? "Hide" : "Read"}
          </Text>
        </HStack>
      </Flex>

      <Collapse in={isOpen} animateOpacity>
        <Box px="14px" pb="14px">
          {isLoading && (
            <HStack spacing={2} py={2}>
              <Spinner size="xs" />
              <Text fontSize="12px" color="gray.500">Loading the trail…</Text>
            </HStack>
          )}
          {isError && (
            <Text fontSize="12px" color="esg_red" py={2}>
              Could not load this run&apos;s trail.
            </Text>
          )}
          {detail && (
            <>
              {/* Rendered with the same ReactMarkdown + remarkGfm as the pre-commit preview, so the
                  document reads identically before and after committing. It IS the same bytes — the
                  server stored what the preview showed. */}
              <Box
                bg="gray.50"
                borderRadius="8px"
                p="14px"
                maxH="420px"
                overflowY="auto"
                fontSize="13px"
                sx={{
                  "& h1": { fontSize: "15px", fontWeight: 700, mt: 2, mb: 1 },
                  "& h2": { fontSize: "14px", fontWeight: 700, mt: 3, mb: 1 },
                  "& h3": { fontSize: "13px", fontWeight: 600, mt: 3, mb: 1 },
                  "& p": { mb: 2 },
                  "& ul": { pl: 5, mb: 2 },
                  "& li": { mb: 1 },
                  "& code": { fontSize: "12px", bg: "gray.100", px: 1, borderRadius: "3px" },
                  "& pre": { bg: "gray.100", p: 2, borderRadius: "6px", overflowX: "auto", mb: 2 },
                  "& pre code": { bg: "transparent", p: 0 },
                  "& table": { fontSize: "12px", mb: 2 },
                  "& th, & td": { border: "1px solid", borderColor: "gray.200", px: 2, py: 1 },
                }}
              >
                {detail.trailMarkdown ? (
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {detail.trailMarkdown}
                  </ReactMarkdown>
                ) : (
                  <Text fontSize="12px" color="gray.500" fontStyle="italic">
                    This run recorded no trail.
                  </Text>
                )}
              </Box>

              {/* The facts a reader needs to trust the document above: which model produced it, which
                  descriptor version it was validated against, and the digest of the file it came from. */}
              <HStack spacing={4} mt="10px" flexWrap="wrap">
                <Fact label="Run" value={`#${run.id}`} />
                {run.agentModel && <Fact label="Model" value={run.agentModel} />}
                {run.templateVersion && <Fact label="Structure" value={run.templateVersion} />}
                {run.sourceFileSha256 && (
                  <Fact label="Source SHA-256" value={run.sourceFileSha256.slice(0, 16)} />
                )}
                {Array.isArray(detail.steps) && (
                  <Fact label="Steps logged" value={String(detail.steps.length)} />
                )}
              </HStack>
            </>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

const Fact = ({ label, value }) => (
  <HStack spacing={1.5}>
    <Text fontSize="10px" color="gray.400" textTransform="uppercase" letterSpacing="0.04em">
      {label}
    </Text>
    <Text fontSize="11px" color="gray.600" fontFamily="mono">
      {value}
    </Text>
  </HStack>
);

export default AiIngestHistory;
